import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box } from '@mui/material';
import { useOutletContext, useNavigate } from 'react-router-dom';

import UnitHeader from '../../components/Unit/UnitHeader';
import UnitFilters from '../../components/Unit/UnitFilters';
import UnitTable from '../../components/Unit/UnitTable';
import CenteredLoader from '../../../../components/loaders/PageLoader';

import unitStyles from './Unit.styles';

import { useUnits } from '../../hooks/useUnits';
import { useOperatorParam } from '../../hooks/useOperatorParam';
import { normalizeUnitRows, uniqueNames, uniqueOperators, ddmmyyyyToDate } from '../../utils/unit.helpers';

import { useCrumb } from '../../../../layouts/TrailProvider';

export default function Unit() {
  const { isSidebarExpanded } = useOutletContext();
  const navigate = useNavigate();

  useCrumb('Units', '/home/units');

  const { raw, loading } = useUnits();
  const operatorParam = useOperatorParam();

  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [filters, setFilters] = useState({ name: [], operator: [], error: [] });

  useEffect(() => {
    setFilters((prev) => {
      const current = prev.operator.slice().sort().join(',');
      return current === operatorParam
        ? prev
        : { ...prev, operator: operatorParam.split(',').filter(Boolean) };
    });
  }, [operatorParam]);

  const rows = useMemo(() => normalizeUnitRows(raw), [raw]);

  const nameOptions = useMemo(() => uniqueNames(rows), [rows]);
  const operatorOptions = useMemo(() => uniqueOperators(rows), [rows]);

  const filteredRows = useMemo(() => {
    const start = dateRange.start ? new Date(dateRange.start) : null;
    const end = dateRange.end ? new Date(dateRange.end) : null;

    return rows.filter((r) => {
      if (filters.name.length && !filters.name.includes(r.name)) return false;
      if (filters.operator.length && !filters.operator.includes(r.operator)) return false;

      if (filters.error.length === 1) {
        const hasError = r.errorsCount > 0;
        if (filters.error[0] === 'Yes' && !hasError) return false;
        if (filters.error[0] === 'No' && hasError) return false;
      }

      // Date filtering: use ISO for comparison
      if (start || end) {
        const iso = r.effectiveFrom?.iso;
        if (!iso) return false;
        const d = new Date(iso);
        if ((start && d < start) || (end && d > end)) return false;
      }

      if (searchQuery) {
        const hay = `${r.name} ${r.effectiveFrom?.display} ${r.operator}`.toLowerCase();
        if (!hay.includes(searchQuery.toLowerCase())) return false;
      }

      return true;
    });
  }, [rows, filters, dateRange, searchQuery]);

  const handleAddUnit = useCallback(() => navigate('/home/create-config'), [navigate]);

  return (
    <Box
      sx={{
        ...unitStyles.unitContent,
        marginLeft: isSidebarExpanded ? '240px' : '95px',
        width: isSidebarExpanded ? 'calc(100vw - 240px)' : 'calc(100vw - 95px)',
        transition: 'all 0.3s ease',
      }}
    >
      {loading ? (
        <CenteredLoader />
      ) : (
        <>
          <UnitHeader
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onSearch={() => {}}
          />

          <UnitFilters
            filters={filters}
            setFilters={setFilters}
            dateRange={dateRange}
            setDateRange={setDateRange}
            nameOptions={nameOptions}
            operatorOptions={operatorOptions}
            errorOptions={['Yes', 'No']}
            onAdd={handleAddUnit}
          />

          <UnitTable rows={filteredRows} actionsEnabled />
        </>
      )}
    </Box>
  );
}
